import { toastr } from 'react-redux-toastr';
import i18n from 'i18next';
import { axiosInstance, ENDPOINT } from '../../../common/constants';
import { handleResponse } from '../../../common/helpers';
import {
  ANNOTATIONS_REQUEST,
  ANNOTATIONS_SUCCESS,
  ANNOTATIONS_FAILURE,
  CLEAR_ANNOTATIONS,
  SET_ANNOTATIONS_FILTER,
  TOGGLE_ANNOTATION_TYPE,
  SET_ANNOTATIONS_VISIBLE,
  SET_ANNOTATION_CREATE_MODE,
  SET_ACTIVE_ANNOTATION_ENTITY
} from './annotations.types';

// Stamped on each fetch so the reducer can drop out-of-order (stale) responses.
let annotationsRequestSeq = 0;

// ===== LOAD ANNOTATIONS =====
export const loadAnnotations = (params) => {
  annotationsRequestSeq += 1;
  const requestId = annotationsRequestSeq;
  const request = queryParams => ({
    type: ANNOTATIONS_REQUEST,
    meta: { params: queryParams, requestId }
  });
  const success = (payload, queryParams) => ({
    type: ANNOTATIONS_SUCCESS,
    payload,
    meta: { params: queryParams, requestId }
  });
  const failure = (error, queryParams) => ({
    type: ANNOTATIONS_FAILURE,
    error,
    meta: { params: queryParams, requestId }
  });

  return (dispatch) => {
    dispatch(request(params));

    return handleResponse(
      axiosInstance.get(ENDPOINT.ANNOTATIONS, { params })
    ).then(
      (payload) => {
        dispatch(success(payload, params));
        return payload;
      },
      (error) => {
        dispatch(failure(error, params));
        const errorSuffix = error && error.message ? `: ${error.message}` : '';
        toastr.error(i18n.t('annotations.toastr.loadFailed', {
          defaultValue: 'Failed to load annotations{{suffix}}',
          suffix: errorSuffix
        }));
        throw error;
      }
    );
  };
};

// ===== CREATE NOTE ANNOTATION =====
// Creates a machine note (point-in-time or time-range) and, on success,
// refreshes the annotations for the current chart by re-fetching with
// reloadParams (bypasses the shouldFetchAnnotations cache).
export const createNote = (noteParams, reloadParams) => dispatch => handleResponse(
  axiosInstance.post(ENDPOINT.ADD_NOTE, noteParams)
).then(
  (response) => {
    toastr.success(i18n.t('annotations.toastr.noteCreated', {
      defaultValue: 'Note added'
    }));
    if (reloadParams) {
      dispatch(loadAnnotations(reloadParams));
    }
    return response;
  },
  (error) => {
    // Show a generic, localized message; raw server/internal error text should
    // not surface in the UI. The detail is still available for diagnostics.
    if (error) {
      // eslint-disable-next-line no-console
      console.error('createNote failed', error);
    }
    toastr.error(i18n.t('annotations.toastr.noteCreateFailed', {
      defaultValue: 'Failed to add note'
    }));
    throw error;
  }
);

// ===== SET ADD-NOTE (CREATE) MODE =====
export const setAnnotationCreateMode = active => ({
  type: SET_ANNOTATION_CREATE_MODE,
  payload: { active: !!active }
});

// ===== SET ACTIVE (FOCUSED) ANNOTATION CHART =====
// Marks which chart the pointer is currently over so create-mode arms only that
// chart. Pass null when the pointer leaves (no chart focused).
export const setActiveAnnotationEntity = entityKey => ({
  type: SET_ACTIVE_ANNOTATION_ENTITY,
  payload: { entityKey: entityKey || null }
});

// ===== CLEAR ANNOTATIONS =====
export const clearAnnotations = () => ({
  type: CLEAR_ANNOTATIONS
});

// ===== SET ANNOTATIONS FILTER =====
export const setAnnotationsFilter = (filterType, value) => ({
  type: SET_ANNOTATIONS_FILTER,
  payload: { filterType, value }
});

// ===== TOGGLE ANNOTATION TYPE VISIBILITY =====
export const toggleAnnotationType = annotationType => ({
  type: TOGGLE_ANNOTATION_TYPE,
  payload: { annotationType }
});

// ===== SET ANNOTATIONS VISIBILITY =====
export const setAnnotationsVisible = visible => ({
  type: SET_ANNOTATIONS_VISIBLE,
  payload: { visible }
});
